import React from 'react';
import { t } from '@lingui/macro';

import { Column } from '@decisiv/ui-components/lib/components/Table/types';

import { SortingTypes } from '@decisiv/ui-components/lib/components/Table';
import ActionsCell from '../Cells/ActionsCell';
import NameCell from '../Cells/NameCell';
import IdCell from '../Cells/IdCell';
import CreatedAtCell from '../Cells/CreatedAtCell';
import TeamKeyCell from '../Cells/TeamKeyCell';
import TeamTypeCell from '../Cells/TeamTypeCell';
import PlatformAccountsCell from '../Cells/PlatformAccountsCell';
import ApiUserCell from '../Cells/ApiUserCell';
import OwnerCell from '../Cells/OwnerCell';
import BillingContactCell from '../Cells/BillingContactCell';
import EnabledAssetsCell from '../Cells/EnabledAssetsCell';
import MembersCell from '../Cells/MembersCell';

import { SERVICE_GROUPS_SORT_COLUMNS } from '../constants';

type ColumnProperties =
  | 'id'
  | 'name'
  | 'createdAt'
  | 'teamKey'
  | 'teamType'
  | 'dealerGroupId'
  | 'apiUsername'
  | 'owner'
  | 'billingContact'
  | 'totals'
  | 'enabled'
  | 'dataRefreshEnabled'
  | 'platformAccounts'
  | 'apiCredentialsValid'
  | 'scheduledForDeletionAt';

export type ColumnName = keyof typeof SERVICE_GROUPS_SORT_COLUMNS;
export type TeamTableColumns = Pick<SentinelTeam, ColumnProperties>;

type Props = {
  onSort: (column: string, direction: SortingTypes) => void;
  order?: ColumnName[];
  except?: ColumnName[];
};

const useTeamsColumns = ({
  onSort,
  except = [],
  order = [
    'NAME',
    'ID',
    'CREATED_AT',
    'TEAM_KEY',
    'TEAM_TYPE',
    'DEALER_GROUP_ID',
    'API_USER',
    'OWNER_EMAIL',
    'BILLING_CONTACT',
    'ENABLED_ASSET_COUNT',
    'MEMBERS',
    'STATE',
  ],
}: Props) => {
  let columns: Column<TeamTableColumns>[] = [
    {
      name: SERVICE_GROUPS_SORT_COLUMNS.NAME,
      DataCell: NameCell,
      getCellKey: ({ id }) => `team-name-${id}`,
      sortBy: (direction) =>
        onSort(SERVICE_GROUPS_SORT_COLUMNS.NAME, direction),
      title: t`Name`,
      fixed: true,
    },
    {
      name: SERVICE_GROUPS_SORT_COLUMNS.ID,
      DataCell: IdCell,
      getCellKey: ({ id }) => `team-id-${id}`,
      sortBy: (direction) => onSort(SERVICE_GROUPS_SORT_COLUMNS.ID, direction),
      title: t`ID`,
    },
    {
      name: SERVICE_GROUPS_SORT_COLUMNS.CREATED_AT,
      DataCell: CreatedAtCell,
      getCellKey: ({ id }) => `team-createdAt-${id}`,
      sortBy: (direction) =>
        onSort(SERVICE_GROUPS_SORT_COLUMNS.CREATED_AT, direction),
      title: t`Created`,
    },
    {
      name: SERVICE_GROUPS_SORT_COLUMNS.TEAM_KEY,
      DataCell: TeamKeyCell,
      getCellKey: ({ id }) => `team-key-${id}`,
      sortBy: (direction) =>
        onSort(SERVICE_GROUPS_SORT_COLUMNS.TEAM_KEY, direction),
      title: t`Key`,
    },
    {
      name: SERVICE_GROUPS_SORT_COLUMNS.TEAM_TYPE,
      DataCell: TeamTypeCell,
      getCellKey: ({ id }) => `team-type-${id}`,
      sortBy: (direction) =>
        onSort(SERVICE_GROUPS_SORT_COLUMNS.TEAM_TYPE, direction),
      title: t`Type`,
    },
    {
      name: SERVICE_GROUPS_SORT_COLUMNS.DEALER_GROUP_ID,
      DataCell: PlatformAccountsCell,
      getCellKey: ({ id }) => `team-portal-${id}`,
      sortBy: (direction) =>
        onSort(SERVICE_GROUPS_SORT_COLUMNS.DEALER_GROUP_ID, direction),
      title: t`Portal ID`,
    },
    {
      name: SERVICE_GROUPS_SORT_COLUMNS.API_USER,
      DataCell: ApiUserCell,
      getCellKey: ({ id }) => `team-apiUsername-${id}`,
      sortBy: (direction) =>
        onSort(SERVICE_GROUPS_SORT_COLUMNS.API_USER, direction),
      title: t`API User`,
    },
    {
      name: SERVICE_GROUPS_SORT_COLUMNS.OWNER_EMAIL,
      DataCell: OwnerCell,
      getCellKey: ({ id }) => `team-owner-${id}`,
      sortBy: (direction) =>
        onSort(SERVICE_GROUPS_SORT_COLUMNS.OWNER_EMAIL, direction),
      title: t`Owner`,
    },
    {
      name: SERVICE_GROUPS_SORT_COLUMNS.BILLING_CONTACT,
      DataCell: BillingContactCell,
      getCellKey: ({ id }) => `team-billing-contact-${id}`,
      title: t`Billing Contact`,
    },
    {
      title: t`Enabled Assets`,
      name: SERVICE_GROUPS_SORT_COLUMNS.ENABLED_ASSET_COUNT,
      DataCell: EnabledAssetsCell,
      getCellKey: ({ id }) => `team-enabled-assets-${id}`,
      sortBy: (direction) =>
        onSort(SERVICE_GROUPS_SORT_COLUMNS.ENABLED_ASSET_COUNT, direction),
    },
    {
      name: SERVICE_GROUPS_SORT_COLUMNS.MEMBERS,
      title: t`Members`,
      DataCell: MembersCell,
      getCellKey: ({ id }) => `team-members-${id}`,
    },
    {
      name: SERVICE_GROUPS_SORT_COLUMNS.STATE,
      DataCell: (props: React.ComponentProps<typeof ActionsCell>) => (
        <ActionsCell {...props} showDataRefresh />
      ),
      getCellKey: ({ id }) => `team-state-${id}`,
      sortBy: (direction) =>
        onSort(SERVICE_GROUPS_SORT_COLUMNS.STATE, direction),
      title: t`State`,
      fixed: true,
    },
  ];

  // Filter out columns that are in the except array
  // This allows for dynamic column management
  if (except.length > 0) {
    columns = columns.filter(
      (column) => !except.includes(column.name as ColumnName),
    );
  }

  // Sort columns based on the provided order
  // // If order is not provided, it defaults to the order array
  if (order.length > 0) {
    columns.sort((a, b) => {
      const indexA = order.indexOf(a.name as ColumnName);
      const indexB = order.indexOf(b.name as ColumnName);

      // If both are missing, keep original order
      if (indexA === -1 && indexB === -1) return 0;
      // If only a is missing, put it after b
      if (indexA === -1) return 1;
      // If only b is missing, put b after a
      if (indexB === -1) return -1;

      return indexA - indexB;
    });
  }

  return columns;
};

export default useTeamsColumns;
